/**
 * @fileoverview Form state and handlers for ContactInformation section.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useFieldValidation } from "../../../hooks/useFieldValidation";

const TABLET_COUNTRY_CODE_WIDTH = "85%";
const DESKTOP_COUNTRY_CODE_WIDTH = "115%";
const TABLET_MIN_WIDTH = 768;
const TABLET_MAX_WIDTH = 1023;

export const useContactInformationForm = ({
  company,
  onFieldChange,
  onValidationChange,
}) => {
  const { t } = useTranslation();
  const prevHasErrorsRef = useRef(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isPhone, setIsPhone] = useState(false);

  useEffect(() => {
    const checkTablet = () => {
      setIsTablet(
        window.innerWidth >= TABLET_MIN_WIDTH &&
          window.innerWidth <= TABLET_MAX_WIDTH,
      );
      setIsPhone(window.innerWidth < TABLET_MIN_WIDTH);
    };

    checkTablet();
    window.addEventListener("resize", checkTablet);
    return () => window.removeEventListener("resize", checkTablet);
  }, []);

  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    telefonoDeOficina: company?.telefonoDeOficina || "",
    correoElectronicoPrincipal: company?.correoElectronicoPrincipal || "",
    paginaInternet: company?.paginaInternet || "",
  });

  const [telefonoDeOficina, setTelefonoDeOficina] = useState(
    company?.telefonoDeOficina || "",
  );
  const [correoElectronicoPrincipal, setCorreoElectronicoPrincipal] = useState(
    company?.correoElectronicoPrincipal || "",
  );
  const [paginaInternet, setPaginaInternet] = useState(
    company?.paginaInternet || "",
  );
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    company?.telefonoDeOficinaCodigoPais || null,
  );

  useEffect(() => {
    if (company?.telefonoDeOficina !== undefined) {
      setTelefonoDeOficina(company.telefonoDeOficina);
      handleFieldChange("telefonoDeOficina", company.telefonoDeOficina);
    }
    if (company?.correoElectronicoPrincipal !== undefined) {
      setCorreoElectronicoPrincipal(company.correoElectronicoPrincipal);
      handleFieldChange(
        "correoElectronicoPrincipal",
        company.correoElectronicoPrincipal,
      );
    }
    if (company?.paginaInternet !== undefined) {
      setPaginaInternet(company.paginaInternet);
      handleFieldChange("paginaInternet", company.paginaInternet);
    }
  }, [
    company?.telefonoDeOficina,
    company?.correoElectronicoPrincipal,
    company?.paginaInternet,
    handleFieldChange,
  ]);

  useEffect(() => {
    if (onValidationChange && prevHasErrorsRef.current !== hasErrors) {
      prevHasErrorsRef.current = hasErrors;
      onValidationChange(hasErrors);
    }
  }, [hasErrors, onValidationChange]);

  const handleInputChange = useCallback(
    (field, value) => {
      if (onFieldChange) {
        onFieldChange(field, value);
      }
    },
    [onFieldChange],
  );

  const handleValidatedFieldChange = useCallback(
    (fieldName, value) => {
      handleFieldChange(fieldName, value);
      handleInputChange(fieldName, value);
    },
    [handleFieldChange, handleInputChange],
  );

  const handleCountryCodeChange = useCallback(
    (value) => {
      setSelectedCountryCode(value.value);
      handleInputChange("telefonoDeOficinaCodigoPais", value.value);
    },
    [handleInputChange],
  );

  const handleTelefonoChange = useCallback(
    (value) => {
      setTelefonoDeOficina(value);
      handleValidatedFieldChange("telefonoDeOficina", value);
    },
    [handleValidatedFieldChange],
  );

  const handleEmailChange = useCallback(
    (value) => {
      setCorreoElectronicoPrincipal(value);
      handleValidatedFieldChange("correoElectronicoPrincipal", value);
    },
    [handleValidatedFieldChange],
  );

  const handleWebsiteChange = useCallback(
    (value) => {
      setPaginaInternet(value);
      handleValidatedFieldChange("paginaInternet", value);
    },
    [handleValidatedFieldChange],
  );

  const countryCodeContainerWidth = isTablet
    ? TABLET_COUNTRY_CODE_WIDTH
    : DESKTOP_COUNTRY_CODE_WIDTH;

  return {
    t,
    isTablet,
    isPhone,
    telefonoDeOficina,
    correoElectronicoPrincipal,
    paginaInternet,
    selectedCountryCode,
    countryCodeContainerWidth,
    getErrorMessage,
    getFieldValidationState,
    handleCountryCodeChange,
    handleTelefonoChange,
    handleEmailChange,
    handleWebsiteChange,
  };
};
